import { useCallback, useEffect, type KeyboardEvent, type RefObject } from 'react';

const MAX_FOCUS_ATTEMPTS = 10;

export function getNextItemIndex(
    key: string,
    currentIndex: number,
    itemCount: number,
): number | null {
    if (key === 'ArrowDown') return (currentIndex + 1) % itemCount;
    if (key === 'ArrowUp') return (currentIndex - 1 + itemCount) % itemCount;
    if (key === 'Home') return 0;
    if (key === 'End') return itemCount - 1;
    return null;
}

export function useMenuKeyboard({
    open,
    menuRef,
    triggerRef,
    onClose,
}: {
    open: boolean;
    menuRef: RefObject<HTMLElement>;
    triggerRef: RefObject<HTMLElement>;
    onClose: () => void;
}) {
    const getItems = useCallback(
        () =>
            Array.from(
                menuRef.current?.querySelectorAll<HTMLElement>(
                    '[role="menuitem"]:not([disabled])',
                ) ?? [],
            ),
        [menuRef],
    );

    useEffect(() => {
        if (!open) return;
        // A menu that has just been positioned can still compute as hidden on its first frame
        // (seen with Reduce Motion on), and focus() is a no-op on hidden elements. Retry for a
        // few frames until the first item actually holds focus.
        let frame = 0;
        let attempts = 0;
        const focusFirstItem = () => {
            const first = getItems()[0];
            first?.focus();
            if (first && document.activeElement !== first && attempts < MAX_FOCUS_ATTEMPTS) {
                attempts += 1;
                frame = window.requestAnimationFrame(focusFirstItem);
            }
        };
        frame = window.requestAnimationFrame(focusFirstItem);
        return () => window.cancelAnimationFrame(frame);
    }, [getItems, open]);

    const onMenuKeyDown = (event: KeyboardEvent<HTMLElement>) => {
        const items = getItems();
        const currentIndex = items.findIndex((item) => item === document.activeElement);

        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            onClose();
            triggerRef.current?.focus();
            return;
        }

        if (event.key === 'Tab') {
            event.preventDefault();
            onClose();
            triggerRef.current?.focus();
            return;
        }

        if (items.length === 0 || currentIndex < 0) return;

        const nextIndex = getNextItemIndex(event.key, currentIndex, items.length);

        if (nextIndex !== null) {
            event.preventDefault();
            items[nextIndex]?.focus();
        }
    };

    const closeAndRestoreFocus = () => {
        onClose();
        triggerRef.current?.focus();
    };

    return { onMenuKeyDown, closeAndRestoreFocus };
}
